import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Confirms publication.
 *
 * The one real modal in the builder, and it earns that: publishing is
 * irreversible, it freezes every identifier, and it makes the workflow
 * available to run. A docked panel would let the user click past all three
 * without registering them.
 *
 * What it says is chosen to be the three things a user cannot undo later,
 * rather than a generic "are you sure?" — which asks a question without giving
 * anyone what they need to answer it.
 */
export function PublishDialog({
  workflowName,
  open,
  isPending,
  onOpenChange,
  onConfirm,
}: {
  workflowName: string;
  open: boolean;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing mid-flight would leave the mutation resolving against a
        // dismissed dialog, so the result would land nowhere.
        if (isPending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Publish {workflowName}?</DialogTitle>
          <DialogDescription>
            Publishing makes this workflow available to run, and it cannot be
            returned to draft.
          </DialogDescription>
        </DialogHeader>

        <ul className="text-muted-foreground flex list-disc flex-col gap-1.5 pl-5 text-sm">
          <li>A board appears for it, and tasks can be created on it.</li>
          <li>
            Step and phase identifiers are fixed from then on — tasks reference
            them, so changing one would orphan work.
          </li>
          <li>
            You can keep editing it afterwards: changes save as a draft, and
            reach its board only when you publish them.
          </li>
        </ul>

        <DialogFooter>
          <DialogClose
            render={<Button variant="outline" disabled={isPending} />}
          >
            Cancel
          </DialogClose>
          <Button onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Publishing…' : 'Publish workflow'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
